import React from 'react';
import { isNil, cloneDeep } from 'lodash';
import { StyleEditSections } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSections/StyleEditSections';
import { StyleEditViewSection } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.types';
import { processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { StyleEditSection } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import DividedColumnDetails from 'src/pages/AssortmentBuild/StyleEdit/DividedColumnDetailsSection/DividedColumnDetails';
import {
  StyleEditMultiSectionProps,
  HeaderData,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.types';
import StyleEditMultiSection from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditMultiSection';
import { ConfigApiV2 } from 'configuration-schemas/confdefnView';
import styleEditStyles from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.styles';

type SectionColorHeaderProps = {
  stickyElement?: JSX.Element;
};
export const SectionColorHeader = ({ stickyElement }: SectionColorHeaderProps) => {
  return (
    <React.Fragment>
      <div className={styleEditStyles.headerStylingDecoy}>
        <div onClick={(event) => event.stopPropagation()} />
      </div>
      <div className={styleEditStyles.headerColumnContainer}>
        <div className="columnColorContainer">{stickyElement}</div>
      </div>
    </React.Fragment>
  );
};

function createHeaderData(context: StyleEditSections): HeaderData {
  const { filteredStyleColors, removableStyleColors } = context.props;

  const headerData = filteredStyleColors.reduce((acc: Record<string, any>, child) => {
    const id = child.id ?? child.product;
    acc[id] = cloneDeep(child);
    acc[id]['isremovable'] = removableStyleColors.indexOf(id) >= 0;
    return acc;
  }, {});

  return headerData;
}

export function buildStyleEditSection(
  context: StyleEditSections,
  config: StyleEditViewSection,
  scope: BasicPivotItem,
  expanded: boolean,
  refreshSections: (componentId: string | null) => void,
  currentRefreshCount: number,
  lastEditedSection: string | null,
) {
  const { allStyleColors, existingStyleColors, sortedStyleColorIds, onRefresh } = context.props;
  const styleId = scope.id;
  const configApi: ConfigApiV2 = config.configApi;
  const sectionData = {
    ...config,
    ...(!isNil(configApi) ? { configApi } : undefined),
    ...(!isNil(config.dataApi) ? { dataApi: processApiParams(config.dataApi, scope) } : undefined),
  };

  const showOnlyPreexist = !isNil(config.showOnlyPreexist) ? true : config.showOnlyPreexist;
  return (
    <div style={{ width: '100%' }}>
      {/* @ts-ignore ignored until we get a proper inViewport/not inViewport type */}
      <StyleEditSection
        expanded={expanded}
        existingStyleColors={existingStyleColors}
        sectionData={sectionData}
        headerData={createHeaderData(context)}
        styleId={styleId}
        addColor={context.addColor}
        sortedStyleColorIds={sortedStyleColorIds}
        allStyleColors={allStyleColors}
        refresh={onRefresh}
        onSectionDataLoaded={() => {
          context.setState({
            sectionDataLoaded: true,
          });
        }}
        showOnlyPreexist={showOnlyPreexist}
        refreshSections={refreshSections}
        currentRefreshCount={currentRefreshCount}
        lastEditedSection={lastEditedSection}
      />
    </div>
  );
}

export function createSectionElement(
  context: StyleEditSections,
  config: StyleEditViewSection,
  scope: BasicPivotItem,
  expanded: boolean,
  refreshSections: (componentId: string | null) => void,
  currentRefreshCount: number,
  lastEditedSection: string | null,
) {
  switch (config.componentType) {
    case 'column_form':
    case 'column_form_read_only':
      const dataApi = processApiParams(config.dataApi, scope);
      return (
        <DividedColumnDetails
          styleId={scope.id}
          dataApi={dataApi}
          configApi={config.configApi}
          expanded={expanded}
          refreshSections={refreshSections}
          currentRefreshCount={currentRefreshCount}
          lastEditedSection={lastEditedSection}
          updateCoordinateMap={config.updateCoordinateMap}
        />
      );
    case 'column_grid':
    case 'column_grid_with_button':
      return buildStyleEditSection(
        context,
        config,
        scope,
        expanded,
        refreshSections,
        currentRefreshCount,
        lastEditedSection,
      );
    case 'column_multi_grid':
      const processedDataApi = processApiParams(config.dataApi, scope);
      const styleEditMultiSectionProps: StyleEditMultiSectionProps = {
        expanded,
        dataApi: processedDataApi,
        configApi: config.configApi,
        skipForwardPropagation: !isNil(config.skipForwardPropagation) ? config.skipForwardPropagation : false,
        scope: scope,
        headerData: createHeaderData(context),
        allStyleColors: context.props.allStyleColors,
        existingStyleColors: context.props.existingStyleColors,
        parentId: !isNil(context.props.styleData) ? context.props.styleData.id : '',
        sortedStyleColorIds: context.props.sortedStyleColorIds,
        getHeaderElement: (element: JSX.Element | undefined) => {
          if (!element) {
            context.setState({
              stickyHeaderAdditionElement: undefined,
            });
          } else if (!context.state.stickyHeaderAdditionElement) {
            context.setState({
              stickyHeaderAdditionElement: element,
            });
          }
        },
        onSectionDataLoaded: () => {
          context.setState({
            sectionDataLoaded: true,
          });
        },
        refreshSections: refreshSections,
        currentRefreshCount: currentRefreshCount,
        lastEditedSection,
      };

      return <StyleEditMultiSection {...styleEditMultiSectionProps} />;
    default:
      return <div>Invalid componentType</div>;
  }
}
